import { toast } from 'react-toastify';
import { useSWRConfig } from 'swr';
import { update as updateStatus } from '~/utils/api/DealStatus';
import { isSuccess } from '~/utils/api/api_handler';
import useLoader from '~/hooks/useLoader';
import { StatusHandleReasonButtons } from './StatusHandleReasonButtons';
import { dealCacheKey } from '~/hooks/useDeals';
import type { SurveyAnswer } from '~/models/Survey';
import { PARTIALLY_FINISHED } from '~/models/Status';
import { useTodoTemplates } from '~/hooks/useTodoTemplates';
import { useDealTodos } from '~/hooks/deals/useDealTodos';
import {
  countUncreatedByTemplates,
  filterTodoTemplatesByStatus
} from '~/models/TodoTemplate';
import { useDealContext } from '~/context/DealContext';

type Props = {
  nextStatusId: number;
  prevStatusId?: number | null;
  forwardDisabled?: boolean;
  alertText?: string | null;
  allowPartiallyFinish?: boolean;
};

export const StatusHandleClientButtons: React.FC<Props> = ({
  nextStatusId,
  prevStatusId = null,
  forwardDisabled = false,
  alertText = null,
  allowPartiallyFinish = false
}: Props) => {
  const deal = useDealContext();
  const { mutate: globalMutate } = useSWRConfig();
  const dealTodos = useDealTodos(deal.id);
  const todoTemplates = useTodoTemplates().useIndex().data;
  const { Loader, showLoader, hideLoader } = useLoader();
  if (dealTodos.data === null || todoTemplates === null) {
    return null;
  }
  const uncreatedTodoCount = countUncreatedByTemplates(
    dealTodos.data,
    filterTodoTemplatesByStatus(deal, nextStatusId, todoTemplates)
  );
  const handleUpdateStatus = async (
    statusId: number,
    reason: string | null,
    surveyAnswers: SurveyAnswer[] | null
  ): Promise<void> => {
    showLoader();
    const result = await updateStatus(deal.id, statusId, reason, surveyAnswers);
    if (isSuccess(result)) {
      await globalMutate(dealCacheKey(deal.id));
      hideLoader();
      toast.success('更新しました');
      return;
    }
    hideLoader();
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  const handleForwardStatus = async (): Promise<void> => {
    await handleUpdateStatus(nextStatusId, null, null);
    await dealTodos.mutate();
    if (uncreatedTodoCount !== 0) {
      toast.success(`${uncreatedTodoCount}件のTODOを追加しました。`);
    }
  };
  const handleBackwardStatus = async (reason: string): Promise<void> => {
    if (prevStatusId !== null) {
      await handleUpdateStatus(prevStatusId, reason, null);
    }
  };
  const handleForwardStatusWithSurvey = async (
    surveyAnswers: SurveyAnswer[] | null
  ): Promise<void> => {
    await handleUpdateStatus(nextStatusId, null, surveyAnswers);
  };
  const handlePartiallyFinish = async (): Promise<void> =>
    handleUpdateStatus(PARTIALLY_FINISHED, null, null);

  return (
    <>
      <Loader />
      <StatusHandleReasonButtons
        alertText={alertText}
        canBackward={prevStatusId !== null}
        deal={deal}
        forwardDisabled={forwardDisabled}
        handleBackwardStatus={handleBackwardStatus}
        handleForwardStatus={handleForwardStatus}
        handleForwardStatusWithSurvey={handleForwardStatusWithSurvey}
        handlePartiallyFinish={
          allowPartiallyFinish ? handlePartiallyFinish : null
        }
        nextStatusId={nextStatusId}
      />
    </>
  );
};
